/**
 * ─────────────────────────────────────────────
 * src/store/slices/projectSlice.js
 * ─────────────────────────────────────────────
 *
 * Manages the selected project detail view used in Projects.js:
 *   - selectedProject (full project with summary data)
 *   - columns (lists + tasks for that project)
 *   - allUsers (for member/assignee picker)
 *   - member add/remove operations
 *   - task creation from Projects.js activity table
 *   - project creation (POST /api/projects)
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import projectService from "../../services/projectService";

const extractError = (error) =>
  error?.response?.data?.message || error?.message || "Something went wrong.";

// ─────────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────────

// Create a new project (called from Layout.js modal)
export const createProject = createAsyncThunk(
  "project/createProject",
  async (projectData, { rejectWithValue }) => {
    try {
      const data = await projectService.createProject(projectData);
      return data.data; // { project, list, task }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Fetch single project by ID (used after adding member)
export const fetchProjectById = createAsyncThunk(
  "project/fetchProjectById",
  async (projectId, { rejectWithValue }) => {
    try {
      const data = await projectService.getProjectById(projectId);
      return data.data; // project object
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Fetch lists + tasks for the selected project
// Used by Projects.js when a project is opened
export const fetchProjectLists = createAsyncThunk(
  "project/fetchProjectLists",
  async (projectId, { rejectWithValue }) => {
    try {
      const data = await projectService.getProjectLists(projectId);
      return data.data; // array of lists with tasks
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Fetch project summary (trackingStatus, daysLeft, members, timeline)
export const fetchProjectSummary = createAsyncThunk(
  "project/fetchProjectSummary",
  async (projectId, { rejectWithValue }) => {
    try {
      const data = await projectService.getProjectSummary(projectId);
      return data.data; // full summary object
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Add a member to the selected project
export const addProjectMember = createAsyncThunk(
  "project/addProjectMember",
  async ({ projectId, userId }, { rejectWithValue }) => {
    try {
      const data = await projectService.addMember(projectId, userId);
      return data.data; // updated project
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Remove a member from the selected project
export const removeProjectMember = createAsyncThunk(
  "project/removeProjectMember",
  async ({ projectId, userId }, { rejectWithValue }) => {
    try {
      await projectService.removeMember(projectId, userId);
      return userId; // return userId so reducer can filter it out
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Fetch all users (member picker + assignee picker)
export const fetchAllUsers = createAsyncThunk(
  "project/fetchAllUsers",
  async (picker = false, { rejectWithValue }) => {
    try {
      const data = await projectService.getAllUsers(picker);
      return data.data; // array of users
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);
// Update project (name, description, or dates)
export const updateProject = createAsyncThunk(
  "project/updateProject",
  async ({ projectId, updates }, { rejectWithValue }) => {
    try {
      const data = await projectService.updateProject(projectId, updates);
      return data.data; // updated project
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);
export const disableProject = createAsyncThunk(
  "project/disableProject",
  async (projectId, { rejectWithValue }) => {
    try {
      const data = await projectService.disableProject(projectId);
      return data.data; // updated project
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const enableProject = createAsyncThunk(
  "project/enableProject",
  async (projectId, { rejectWithValue }) => {
    try {
      const data = await projectService.enableProject(projectId);
      return data.data; // updated project
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);
// ─────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────
const initialState = {
  // The project currently open in Projects.js detail view
  selectedProject: null,

  // Lists + tasks for the selected project
  // Same shape as what the board uses: [{ _id, name, tasks: [...] }]
  columns: [],

  // All users for member/assignee picker
  allUsers: [],

  // Loading flags
  isCreatingProject: false,
  isFetchingProject: false,
  isFetchingLists: false,
  isFetchingSummary: false,
  isAddingMember: false,
  isRemovingMember: false,
  isFetchingUsers: false,

  // Errors
  createProjectError: null,
  projectError: null,
  listsError: null,
  membersError: null,

  // Success flag for project creation
  createProjectSuccess: false,
  isUpdatingProject: false,
updateProjectError: null,
};

// ─────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────
const projectSlice = createSlice({
  name: "project",
  initialState,

  reducers: {
    // Set the selected project (called when user clicks a project row)
    setSelectedProject: (state, action) => {
      state.selectedProject = action.payload;
    },

    // Clear selected project (called when user clicks Back)
    clearSelectedProject: (state) => {
      state.selectedProject = null;
      state.columns = [];
    },

    // Update columns directly (used by Kanban and Projects for local updates)
    setColumns: (state, action) => {
      state.columns = action.payload;
    },

    // Add a task to a specific column locally (optimistic update)
    addTaskToColumn: (state, action) => {
      const { listId, task } = action.payload;
      state.columns = state.columns.map((col) =>
        col._id === listId
          ? { ...col, tasks: [...(col.tasks || []), task] }
          : col,
      );
    },

    // ✅ Fix — clearAllUsers as its own separate reducer
    removeTaskFromColumn: (state, action) => {
      const { listId, taskId } = action.payload;
      state.columns = state.columns.map((col) =>
        col._id === listId
          ? { ...col, tasks: col.tasks.filter((t) => t._id !== taskId) }
          : col,
      );
    },

    clearAllUsers: (state) => {
      state.allUsers = [];
    },

    // Reset create project state after navigation
    resetCreateProject: (state) => {
      state.createProjectSuccess = false;
      state.createProjectError = null;
      state.isCreatingProject = false;
    },

    clearProjectErrors: (state) => {
      state.createProjectError = null;
      state.projectError = null;
      state.listsError = null;
      state.membersError = null;
        state.updateProjectError = null;
    },
  },

  extraReducers: (builder) => {
    // ── createProject ─────────────────────────
    builder
      .addCase(createProject.pending, (state) => {
        state.isCreatingProject = true;
        state.createProjectError = null;
        state.createProjectSuccess = false;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.isCreatingProject = false;
        state.createProjectSuccess = true;
        // action.payload.project is the new project object
      })
      .addCase(createProject.rejected, (state, action) => {
        state.isCreatingProject = false;
        state.createProjectError = action.payload;
      });
     // ── updateProject ─────────────────────────
builder
  .addCase(updateProject.pending, (state) => {
    state.isUpdatingProject = true;
    state.updateProjectError = null;
  })
  .addCase(updateProject.fulfilled, (state, action) => {
    state.isUpdatingProject = false;
    const updatedProject = action.payload?.project ?? action.payload?.data ?? action.payload;
    if (updatedProject) {
      // Don't let an edit response's stripped/empty members list wipe the
      // populated members already in state.
      const incoming = { ...updatedProject };
      const incomingMembers = incoming.members;
      const hasPopulatedIncoming =
        Array.isArray(incomingMembers) &&
        incomingMembers.length > 0 &&
        typeof incomingMembers[0] === "object";
      if (!hasPopulatedIncoming) delete incoming.members;
      state.selectedProject = {
        ...state.selectedProject,
        ...incoming,
      };
    }
  })
  .addCase(updateProject.rejected, (state, action) => {
    state.isUpdatingProject = false;
    state.updateProjectError = action.payload;
  });

  // ── disableProject / enableProject ────────
builder
  .addCase(disableProject.pending, (state) => {
    state.isUpdatingProject = true;
    state.updateProjectError = null;
  })
  .addCase(disableProject.fulfilled, (state, action) => {
    state.isUpdatingProject = false;
    if (action.payload) {
      const incoming = { ...action.payload };
      const hasPopulatedMembers =
        Array.isArray(incoming.members) &&
        incoming.members.length > 0 &&
        typeof incoming.members[0]?.user === "object";
      if (!hasPopulatedMembers) delete incoming.members;
      if (typeof incoming.createdBy !== "object") delete incoming.createdBy;
      state.selectedProject = { ...state.selectedProject, ...incoming };
    }
  })
  .addCase(disableProject.rejected, (state, action) => {
    state.isUpdatingProject = false;
    state.updateProjectError = action.payload;
  });

builder
  .addCase(enableProject.pending, (state) => {
    state.isUpdatingProject = true;
    state.updateProjectError = null;
  })
  .addCase(enableProject.fulfilled, (state, action) => {
    state.isUpdatingProject = false;
    if (action.payload) {
      const incoming = { ...action.payload };
      const hasPopulatedMembers =
        Array.isArray(incoming.members) &&
        incoming.members.length > 0 &&
        typeof incoming.members[0]?.user === "object";
      if (!hasPopulatedMembers) delete incoming.members;
      if (typeof incoming.createdBy !== "object") delete incoming.createdBy;
      state.selectedProject = { ...state.selectedProject, ...incoming };
    }
  })
  .addCase(enableProject.rejected, (state, action) => {
    state.isUpdatingProject = false;
    state.updateProjectError = action.payload;
  });
    // ── fetchProjectById ──────────────────────
    builder
      .addCase(fetchProjectById.pending, (state) => {
        state.isFetchingProject = true;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.isFetchingProject = false;
        // Merge new data into selectedProject (keeps UI fields intact).
        // Never let a stripped/empty members list overwrite the populated
        // one that the summary endpoint provides.
        const incoming = { ...action.payload };
        const incomingMembers = incoming.members;
        const hasPopulatedIncoming =
          Array.isArray(incomingMembers) &&
          incomingMembers.length > 0 &&
          typeof incomingMembers[0] === "object";
        if (!hasPopulatedIncoming) delete incoming.members;
        state.selectedProject = {
          ...state.selectedProject,
          ...incoming,
        };
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.isFetchingProject = false;
        state.projectError = action.payload;
      });

    // ── fetchProjectLists ─────────────────────
    builder
      .addCase(fetchProjectLists.pending, (state) => {
        state.isFetchingLists = true;
        state.listsError = null;
      })
      .addCase(fetchProjectLists.fulfilled, (state, action) => {
        state.isFetchingLists = false;
        // Normalize: ensure every list has a tasks array
        state.columns = action.payload.map((list) => ({
          ...list,
          tasks: Array.isArray(list.tasks) ? list.tasks : [],
        }));
      })
      .addCase(fetchProjectLists.rejected, (state, action) => {
        state.isFetchingLists = false;
        state.listsError = action.payload;
      });

    // ── fetchProjectSummary ───────────────────
    builder
      .addCase(fetchProjectSummary.pending, (state) => {
        state.isFetchingSummary = true;
      })
      .addCase(fetchProjectSummary.fulfilled, (state, action) => {
        state.isFetchingSummary = false;
        // Merge summary data into selectedProject
        state.selectedProject = {
          ...state.selectedProject,
          ...action.payload,
        };
      })
      .addCase(fetchProjectSummary.rejected, (state) => {
        state.isFetchingSummary = false;
      });

    // ── addProjectMember ──────────────────────
    builder
      .addCase(addProjectMember.pending, (state) => {
        state.isAddingMember = true;
        state.membersError = null;
      })
      .addCase(addProjectMember.fulfilled, (state, action) => {
        state.isAddingMember = false;
        // Update members in selectedProject from API response
        if (state.selectedProject) {
          state.selectedProject = {
            ...state.selectedProject,
            members: action.payload.members,
          };
        }
      })
      .addCase(addProjectMember.rejected, (state, action) => {
        state.isAddingMember = false;
        state.membersError = action.payload;
      });

    // ── removeProjectMember ───────────────────
    builder
      .addCase(removeProjectMember.pending, (state) => {
        state.isRemovingMember = true;
      })
      .addCase(removeProjectMember.fulfilled, (state, action) => {
        state.isRemovingMember = false;
        // Remove member locally from selectedProject
        if (state.selectedProject?.members) {
          state.selectedProject.members = state.selectedProject.members.filter(
            (m) => m._id !== action.payload,
          );
        }
      })
      .addCase(removeProjectMember.rejected, (state, action) => {
        state.isRemovingMember = false;
        state.membersError = action.payload;
      });

    // ── fetchAllUsers ─────────────────────────
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.isFetchingUsers = true;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.isFetchingUsers = false;
        state.allUsers = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        state.isFetchingUsers = false;
      });
  },
});

export const {
  setSelectedProject,
  clearSelectedProject,
  setColumns,
  addTaskToColumn,
  removeTaskFromColumn,
  resetCreateProject,
  clearProjectErrors,
  clearAllUsers,
} = projectSlice.actions;

export default projectSlice.reducer;
